import { createResource, createSignal, For, Show } from "solid-js";
import { useApi, isOk } from "../api";
import { firstError } from "../api/envelope";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import Text from "../components/Text";
import Modal from "../components/Modal";
import { showConfirm } from "../components/Dialog";
import { Badge } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import { formatDateTime } from "../services/dateFormat";
import { WebhookKind, type WebhookDto, type WebhookTestResponse } from "../api/generated/schemas";
import styles from "./Webhooks.module.css";

// The backend serializes WebhookKind as an integer, so orval names the members NUMBER_0/NUMBER_1.
// Naming them once here keeps that out of the rest of the page.
const KIND_GENERIC = WebhookKind.NUMBER_0;
const KIND_DISCORD = WebhookKind.NUMBER_1;

interface DraftWebhook {
    kind: WebhookKind;
    name: string;
    url: string;
    usernameOverride: string;
    avatarUrl: string;
}

const emptyDraft = (): DraftWebhook => ({
    kind: KIND_DISCORD,
    name: "",
    url: "",
    usernameOverride: "",
    avatarUrl: "",
});

export default function Webhooks() {
    const api = useApi();
    const { t } = useTranslation();
    const toast = useToast();

    const [editing, setEditing] = createSignal<WebhookDto | null>(null);
    const [draft, setDraft] = createSignal<DraftWebhook>(emptyDraft());
    const [formOpen, setFormOpen] = createSignal(false);
    const [saving, setSaving] = createSignal(false);
    const [testingId, setTestingId] = createSignal<string | null>(null);
    const [testResults, setTestResults] = createSignal<Record<string, WebhookTestResponse>>({});

    const [webhooks, { refetch }] = createResource(async () => {
        const res = await api.webhook.list();
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t("WEBHOOK_LOAD_FAILED"));
            return [] as WebhookDto[];
        }
        return res.data.value!.webhooks;
    });

    const may = (webhook: WebhookDto, action: string) => webhook.myActions.includes(action);

    const openCreate = () => {
        setEditing(null);
        setDraft(emptyDraft());
        setFormOpen(true);
    };

    const openEdit = (webhook: WebhookDto) => {
        setEditing(webhook);
        setDraft({
            kind: webhook.kind,
            name: webhook.name,
            url: "",
            usernameOverride: webhook.usernameOverride ?? "",
            avatarUrl: webhook.avatarUrl ?? "",
        });
        setFormOpen(true);
    };

    const save = async () => {
        const current = draft();
        setSaving(true);
        try {
            const target = editing();
            const res = target
                ? await api.webhook.update(target.id, {
                      name: current.name,
                      url: current.url.trim() === "" ? null : current.url.trim(),
                      usernameOverride: current.usernameOverride,
                      avatarUrl: current.avatarUrl,
                  })
                : await api.webhook.create({
                      kind: current.kind,
                      name: current.name,
                      url: current.url.trim(),
                      usernameOverride: current.usernameOverride,
                      avatarUrl: current.avatarUrl,
                  });

            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t("WEBHOOK_SAVE_FAILED"));
                return;
            }

            toast?.success(t("WEBHOOK_SAVED"));
            setFormOpen(false);
            await refetch();
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async (webhook: WebhookDto) => {
        setTestingId(webhook.id);
        try {
            const res = await api.webhook.sendTest(webhook.id);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t("WEBHOOK_TEST_FAILED"));
                return;
            }
            setTestResults({ ...testResults(), [webhook.id]: res.data.value! });
        } finally {
            setTestingId(null);
        }
    };

    const remove = async (webhook: WebhookDto) => {
        if (!(await showConfirm(t("WEBHOOK_DELETE_CONFIRM", webhook.name), true, { confirmLabel: t("DELETE") }))) return;

        const res = await api.webhook.delete(webhook.id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t("WEBHOOK_DELETE_FAILED"));
            return;
        }
        toast?.success(t("WEBHOOK_DELETED"));
        await refetch();
    };

    const canSave = () => {
        const current = draft();
        if (current.name.trim() === "") return false;
        return editing() != null || current.url.trim() !== "";
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <div class={styles.titleBlock}>
                    <h1><Text val="WEBHOOKS_TITLE" /></h1>
                    <p class={styles.subtitle}><Text val="WEBHOOKS_SUBTITLE" /></p>
                </div>
                <div class={styles.headerActions}>
                    <button class="btn btn-primary" onClick={openCreate}>
                        <i class="fa-solid fa-plus" /> <Text val="WEBHOOK_NEW" />
                    </button>
                </div>
            </div>

            <Show
                when={webhooks.state !== "pending"}
                fallback={<div class={styles.grid}><For each={Array(3).fill(0)}>{() => <div class={styles.skeletonCard} />}</For></div>}
            >
                <Show
                    when={(webhooks()?.length ?? 0) > 0}
                    fallback={
                        <EmptyState
                            illustration="notifications"
                            title={t("WEBHOOKS_EMPTY_TITLE")}
                            hint={t("WEBHOOKS_EMPTY_HINT")}
                            action={<button class="btn btn-primary" onClick={openCreate}><Text val="WEBHOOK_NEW" /></button>}
                        />
                    }
                >
                    <div class={styles.grid}>
                        <For each={webhooks()!}>
                            {(webhook) => (
                                <div class={`fcard ${styles.card}`}>
                                    <div class={styles.cardHead}>
                                        <div class={styles.cardTitle}>
                                            <span class={styles.cardName}>{webhook.name}</span>
                                            <Badge tone={webhook.kind === KIND_DISCORD ? "accent" : "neutral"} pill>
                                                <Text val={webhook.kind === KIND_DISCORD ? "WEBHOOK_KIND_DISCORD" : "WEBHOOK_KIND_GENERIC"} />
                                            </Badge>
                                        </div>
                                    </div>

                                    <div class="v" style={{ gap: "var(--spacing-xs)" }}>
                                        <span class={styles.metaLabel}><Text val="WEBHOOK_DESTINATION" /></span>
                                        <span class={styles.destination}>{webhook.maskedUrl}</span>
                                    </div>

                                    <div class={styles.cardMeta}>
                                        <div class="v" style={{ gap: "var(--spacing-xs)" }}>
                                            <span class={styles.metaLabel}><Text val="WEBHOOK_CREATED" /></span>
                                            <span class={styles.metaValue}>{formatDateTime(webhook.createdAt)}</span>
                                        </div>
                                        <Show when={webhook.usernameOverride}>
                                            <div class="v" style={{ gap: "var(--spacing-xs)" }}>
                                                <span class={styles.metaLabel}><Text val="WEBHOOK_USERNAME" /></span>
                                                <span class={styles.metaValue}>{webhook.usernameOverride}</span>
                                            </div>
                                        </Show>
                                    </div>

                                    <Show when={testResults()[webhook.id]}>
                                        {(result) => (
                                            <div class={styles.testResult}>
                                                <i
                                                    class={`fa-solid ${result().delivered ? "fa-circle-check" : "fa-circle-exclamation"} ${result().delivered ? styles.testOk : styles.testFailed}`}
                                                />
                                                <span>
                                                    <Show when={result().delivered} fallback={result().error}>
                                                        {t("WEBHOOK_TEST_OK", result().durationMs)}
                                                    </Show>
                                                </span>
                                            </div>
                                        )}
                                    </Show>

                                    <div class={styles.cardActions}>
                                        <Show when={may(webhook, "webhook.test")}>
                                            <button
                                                class="btn btn-secondary btn-sm"
                                                disabled={testingId() === webhook.id}
                                                onClick={() => void sendTest(webhook)}
                                            >
                                                <Show when={testingId() === webhook.id} fallback={<><i class="fa-solid fa-paper-plane" /> <Text val="WEBHOOK_TEST" /></>}>
                                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="WEBHOOK_TESTING" />
                                                </Show>
                                            </button>
                                        </Show>
                                        <Show when={may(webhook, "webhook.config.write")}>
                                            <button class="btn btn-secondary btn-sm" onClick={() => openEdit(webhook)}>
                                                <i class="fa-solid fa-pen-to-square" /> <Text val="EDIT" />
                                            </button>
                                        </Show>
                                        <Show when={may(webhook, "webhook.delete")}>
                                            <button class="btn btn-danger btn-sm" onClick={() => void remove(webhook)}>
                                                <i class="fa-solid fa-trash" /> <Text val="DELETE" />
                                            </button>
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>

            <Modal
                open={formOpen()}
                onClose={() => setFormOpen(false)}
                title={editing() ? t("WEBHOOK_EDIT_TITLE") : t("WEBHOOK_NEW_TITLE")}
                busy={saving()}
            >
                <form class="v" style={{ gap: "var(--spacing-md)" }} onSubmit={(e) => { e.preventDefault(); void save(); }}>
                    <Show when={!editing()}>
                        <label class={styles.field}>
                            <span><Text val="WEBHOOK_KIND" /></span>
                            <select
                                value={String(draft().kind)}
                                onChange={(e) => setDraft({ ...draft(), kind: Number(e.currentTarget.value) as WebhookKind })}
                            >
                                <option value={String(KIND_DISCORD)}>{t("WEBHOOK_KIND_DISCORD")}</option>
                                <option value={String(KIND_GENERIC)}>{t("WEBHOOK_KIND_GENERIC")}</option>
                            </select>
                        </label>
                    </Show>

                    <label class={styles.field}>
                        <span><Text val="WEBHOOK_NAME" /></span>
                        <input
                            type="text"
                            maxLength={200}
                            value={draft().name}
                            placeholder={t("WEBHOOK_NAME_PLACEHOLDER")}
                            onInput={(e) => setDraft({ ...draft(), name: e.currentTarget.value })}
                        />
                    </label>

                    <label class={styles.field}>
                        <span><Text val="WEBHOOK_URL" /></span>
                        <input
                            type="url"
                            value={draft().url}
                            placeholder={editing() ? t("WEBHOOK_URL_KEEP_HINT") : "https://discord.com/api/webhooks/..."}
                            onInput={(e) => setDraft({ ...draft(), url: e.currentTarget.value })}
                        />
                        <span class={styles.fieldHint}>
                            <Text val={editing() ? "WEBHOOK_URL_KEEP_HINT" : "WEBHOOK_URL_HINT"} />
                        </span>
                    </label>

                    <Show when={draft().kind === KIND_DISCORD}>
                        <div class={styles.fieldRow}>
                            <label class={styles.field}>
                                <span><Text val="WEBHOOK_USERNAME" /></span>
                                <input
                                    type="text"
                                    maxLength={80}
                                    value={draft().usernameOverride}
                                    onInput={(e) => setDraft({ ...draft(), usernameOverride: e.currentTarget.value })}
                                />
                                <span class={styles.fieldHint}><Text val="WEBHOOK_USERNAME_HINT" /></span>
                            </label>
                            <label class={styles.field}>
                                <span><Text val="WEBHOOK_AVATAR" /></span>
                                <input
                                    type="url"
                                    value={draft().avatarUrl}
                                    onInput={(e) => setDraft({ ...draft(), avatarUrl: e.currentTarget.value })}
                                />
                                <span class={styles.fieldHint}><Text val="WEBHOOK_AVATAR_HINT" /></span>
                            </label>
                        </div>
                    </Show>

                    <div class={styles.formActions}>
                        <button type="button" class="btn btn-secondary" onClick={() => setFormOpen(false)}>
                            <Text val="CANCEL" />
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={saving() || !canSave()}>
                            <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE" /></>}>
                                <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE" />
                            </Show>
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
